import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type RateInputProps = {
  rate: number
  defaultValue?: number
  setRate: React.Dispatch<React.SetStateAction<number>>
  holdDelayMs?: number
  holdIntervalMs?: number
  prefix?: 'gt' | 'gte' | 'lt' | 'lte'
  step?: number
}

const prefixMap: Record<NonNullable<RateInputProps['prefix']>, string> = {
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
}

const RateInput = ({
  rate,
  defaultValue = 5,
  setRate,
  holdDelayMs = 350,
  holdIntervalMs = 80,
  prefix,
  step = 0.25,
}: RateInputProps) => {
  const [inputValueWidth, setInputValueWidth] = useState<number>(0)
  const measureRef = useRef<HTMLSpanElement>(null)
  const holdTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (defaultValue && !rate) {
      setRate(defaultValue)
    }
  }, [defaultValue, rate, setRate])

  const stopHold = useCallback(() => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current)
      holdTimeoutRef.current = null
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current)
      holdIntervalRef.current = null
    }
  }, [])

  const updateRate = useCallback(
    (delta: number) => {
      setRate((current) => current + delta)
    },
    [setRate],
  )

  const startHold = useCallback(
    (delta: number) => {
      stopHold()
      updateRate(delta)
      holdTimeoutRef.current = setTimeout(() => {
        holdIntervalRef.current = setInterval(() => {
          updateRate(delta)
        }, holdIntervalMs)
      }, holdDelayMs)
    },
    [holdDelayMs, holdIntervalMs, stopHold, updateRate],
  )

  useEffect(() => {
    return () => stopHold()
  }, [stopHold])

  const prefixIcon = prefix ? prefixMap[prefix] : ''
  const numericRate = Number.isFinite(rate) ? rate : 0
  const displayValue = `${prefixIcon}${numericRate.toFixed(2)}%`
  const measureValue = useMemo(() => displayValue.replace(/[0-9]/g, '8'), [displayValue])

  useEffect(() => {
    if (!measureRef.current) return
    const width = measureRef.current.offsetWidth
    setInputValueWidth((current) => (width > current ? width : current))
  }, [measureValue])

  return (
    <div className="flex items-center justify-center gap-5">
      <button
        type="button"
        className="flex size-[50px] items-center justify-center rounded-full bg-accent"
        onMouseDown={() => startHold(-step)}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
        onTouchStart={() => startHold(-step)}
        onTouchEnd={stopHold}
      >
        <svg width="18" height="2" viewBox="0 0 18 2" fill="none" aria-hidden>
          <path fill="currentColor" d="M9.9 0h7.2a.9.9 0 0 1 0 1.8H.9A.9.9 0 0 1 .9 0h9Z" />
        </svg>
      </button>

      <span
        className="text-center text-[54px] font-normal tabular-nums leading-[75.6px] tracking-[-1.08px] text-primary"
        style={{ width: inputValueWidth || undefined }}
      >
        {displayValue}
      </span>

      <span
        ref={measureRef}
        aria-hidden
        className="pointer-events-none absolute text-center text-[54px] font-normal tabular-nums leading-[75.6px] tracking-[-1.08px] opacity-0"
      >
        {measureValue}
      </span>

      <button
        type="button"
        className="flex size-[50px] items-center justify-center rounded-full bg-accent"
        onMouseDown={() => startHold(step)}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
        onTouchStart={() => startHold(step)}
        onTouchEnd={stopHold}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
          <path
            fill="currentColor"
            d="M17.1 8.1H9.9V.9a.9.9 0 0 0-1.8 0v7.2H.9a.9.9 0 0 0 0 1.8h7.2v7.2a.9.9 0 0 0 1.8 0V9.9h7.2a.9.9 0 0 0 0-1.8Z"
          />
        </svg>
      </button>
    </div>
  )
}

export default RateInput
